import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import NotReadyIcon from '@mui/icons-material/DoNotDisturbAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import cx from 'classnames';

import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../../../enums/ACCESS_ENUM';
import { ReactComponent as TimelineIcon } from '../../../../../assets/icons/timeline.svg';
import { ReactComponent as ComposerIcon } from '../../../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../../../assets/icons/movieCollection.svg';
import { DateFormatter, bytesToGB, findSearchValue, secondsToHours } from '../../../../../helpers';
import { FOLDER_CONTENT_FILTER, NOTIFICATION, PATHS, VIDEOFEED_STATUS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../../../enums';
import useOnClickOutside from '../../../../../helpers/useOnClickOutside';
import StorageIcon from '../../../../../assets/icons/storage.svg';
import { VIDEOFEED_WITH_SIZE } from '../AllContentVideofeeds';
import styles from './AllContentVideo.module.scss';
import TrashButton from '../../../../modules/TrashButton';
import { Tooltip } from '@mui/material';
import NotReady from '../../../../../assets/icons/not-ready';
import Icon from '../../../../VideofeedsList/Videofeed/icon';
import DeleteVideofeedModal from '../../../../VideofeedsList/DeleteVideofeedModal';
import { convertSeconds } from '../../../../Timeline/utils';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { updateVideofeedTitle } from '../../../../../api';
import { MoreIcon } from '../../../../../assets/icons/threedots';
import EditButton from '../../../../modules/EditButton';
import ShareButton from '../../../../modules/ShareButton';
import { useAppDispatch } from '../../../../../store/hooks';
import { updateFeedToShare, updateShowShareModal } from '../../../../../store/videoFeedsList/videoFeedsListSlice';
import { SortOptions } from '../../../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../../../modules/DateSortHeader';
import FileTypeThumbnail from '../../../../modules/FileTypeThumbnail';
import ReplaceThumbnailModal from '../../../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../../../api/videofeeds';
import ThumbnailButton from '../../../../modules/ThumbnailButton';

type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;

type TSharedVideofeed = {
  feed: VIDEOFEED_WITH_SIZE;
  i: number;
  fetchDataCallback?: () => void;
  forceMovieIcon?: boolean;
  referenceTimelines?: VIDEOFEED_WITH_SIZE[];
  feedIndexById?: Record<string, number>;
  isReferenceChild?: boolean;
  embedded?: boolean;
  forceBlueTimelineIcon?: boolean;
  dateSortOption?: SortOptions | null;
};

const AllContentVideo: React.FC<TSharedVideofeed> = ({
  feed,
  i,
  fetchDataCallback,
  referenceTimelines,
  feedIndexById,
  isReferenceChild = false,
  embedded = false,
  forceBlueTimelineIcon = false,
  dateSortOption,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const page = findSearchValue('page');
  const limit = findSearchValue('limit');
  const sort = findSearchValue('sort');
  const search = findSearchValue('search');
  const folderTrail = findSearchValue('folderTrail');
  const contentFilter = findSearchValue('contentFilter');
  const folderIdFromQuery = findSearchValue('folderId');
  const hasTopicRows = (feed?.topics?.length || 0) > 0;
  const isTopLevelTimeline =
    feed.type === VIDEOFEED_TYPE.TIMELINE &&
    (feed.parent_videofeed_id === null || feed.parent_videofeed_id === undefined);
  const isLegacyTopLevelTimelineCollection = isTopLevelTimeline && hasTopicRows;
  const normalizedReferenceTimelines = Array.isArray(referenceTimelines)
    ? referenceTimelines
    : [];
  const displayReferenceTimelines =
    normalizedReferenceTimelines.length > 0
      ? normalizedReferenceTimelines
      : !isReferenceChild && !embedded && isLegacyTopLevelTimelineCollection
        ? [feed]
        : [];
  const uniqueCollectionTimelineIds = new Set(
    displayReferenceTimelines
      .map((timeline) => String(timeline?.id || ''))
      .filter(Boolean),
  );
  if (
    uniqueCollectionTimelineIds.size === 0 &&
    Boolean((feed as any)?.is_collection_root)
  ) {
    uniqueCollectionTimelineIds.add(String(feed.id));
  }
  const collectionTimelineCount = uniqueCollectionTimelineIds.size;
  const hasReferencesSection = collectionTimelineCount > 0;
  const isCollectionRow = !isReferenceChild && !embedded && (isLegacyTopLevelTimelineCollection || hasReferencesSection);
  const isCollectionTimelineRow = isReferenceChild && embedded;
  const rawSizeBytes = Number(
    feed.size || feed.videos.reduce((a, b) => Number(a) + Number(b.size), 0),
  );
  const shouldShowRefDuration =
    isCollectionTimelineRow || Boolean(feed?.duplicate);
  const sourceTitle = isCollectionRow ? feed.collection_name || feed.title : feed.title;
  const [feedName, setFeedName] = useState(sourceTitle);
  const [isEditing, setIsEditing] = useState(false);
  const [referencesExpanded, setReferencesExpanded] = useState(hasReferencesSection);
  const showExpandedReferences = hasReferencesSection && referencesExpanded && !embedded;
  const shellCollectionTarget =
    !embedded &&
    isCollectionRow &&
    (feed?.topics?.length || 0) === 0 &&
    displayReferenceTimelines.length > 0
      ? displayReferenceTimelines[0]
      : null;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(feed.thumbnail_url || '');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const previewUrl = thumbnailUrl || feed?.videos?.[0]?.s3_preview_url || '';
  const canReplaceThumbnail =
    feed.type === VIDEOFEED_TYPE.COMPOSER &&
    !isReferenceChild &&
    !embedded &&
    feed.status === VIDEOFEED_STATUS.DONE;

  useEffect(() => {
    setThumbnailUrl(feed.thumbnail_url || '');
  }, [feed.thumbnail_url]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedName(e.target.value);
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  const handleEdit = async () => {
    if (feedName.trim().length === 0) {
      setFeedName(sourceTitle);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    if (feedName.trim() === sourceTitle.trim()) return;
    await updateVideofeedTitle(feed.id, feedName, {
      renameCollectionOnly: isCollectionRow,
    });
    if (fetchDataCallback) {
      fetchDataCallback();
    }
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const folderElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!folderElement) return;

    const folderRect = folderElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 310;
    const menuHeight = actionMenuRef.current?.offsetHeight || 300;

    let posX = e.clientX - folderRect.left;
    let posY = e.clientY - folderRect.top;

    if (posX + menuWidth > folderRect.width) {
      posX = folderRect.width - menuWidth - 10;
    }

    const p = folderRect.bottom + menuHeight - window.innerHeight;
    if (folderRect.bottom + menuHeight > window.innerHeight) {
      if (p > 90) {
        posY -= menuHeight - 150;
      }
    }

    setMenuPosition({
      x: posX + 20,
      y: posY,
    });

    setShowDotsMenu(true);
  };

  const enableFeedNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const handleShareClick = (e: React.MouseEvent<HTMLDivElement>, shareScope?: 'single' | 'collection') => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(updateFeedToShare({
      ...(feed as any),
      share_scope: shareScope || (isCollectionRow ? 'collection' : 'single'),
    }));
    dispatch(updateShowShareModal(true));
    setShowDotsMenu(false);
  };

  const redirect = (e: CustomEventHandler, videofeed: VIDEOFEED_WITH_SIZE, feedIndex: number) => {
    if (isEditing) return;
    if (e.target !== e.currentTarget) return;
    const resolvedFeed = shellCollectionTarget && videofeed.id === feed.id ? shellCollectionTarget : videofeed;
    const resolvedFeedIndex =
      resolvedFeed.id === videofeed.id
        ? feedIndex
        : feedIndexById?.[String(resolvedFeed.id)] ?? feedIndex;
    const resolvedFolderId = resolvedFeed?.folder?.id || folderIdFromQuery;
    switch (resolvedFeed.type) {
      case VIDEOFEED_TYPE.COMPOSER:
        history.push(
          `${PATHS.COMPOSER_PAGE.path}?feedId=${resolvedFeed.id}&folderId=${resolvedFolderId}&shared=true
          &fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}&clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}
          &pageType=${VIDEOPAGE_TYPE.COMPOSER}&page=${page}&limit=${limit}&sort=${sort}&search=${search}&allContent=true${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
        break;
      case VIDEOFEED_TYPE.MY_COMPOSER:
        history.push(
          `${PATHS.COMPOSER_PAGE.path}?feedId=${resolvedFeed.id}&shared=true&fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}
          &clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}&pageType=${VIDEOFEED_TYPE.MY_COMPOSER}&page=${page}&limit=${limit}&sort=${sort}&search=${search}&allContent=true${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
        break;
      default:
        history.push(
          `${PATHS.TIMELINE.path}?feedId=${resolvedFeed.id}&folderId=${resolvedFolderId}&shared=true
          &fileside=true&feed=${resolvedFeedIndex}&clipId=${resolvedFeed.videos[0].id}&clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}
          &pageType=${VIDEOPAGE_TYPE.TIMELINE}&page=${page}&limit=${limit}&sort=${sort}&search=${search}&allContent=true${
            folderTrail ? `&folderTrail=${folderTrail}` : ''
          }${
            contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
              ? `&contentFilter=${contentFilter}`
              : ''
          }`,
        );
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCollectionMode, setDeleteCollectionMode] = useState(false);

  useEffect(() => {
    setReferencesExpanded((prev) =>
      hasReferencesSection ? prev || hasReferencesSection : false,
    );
  }, [hasReferencesSection]);

  useEffect(() => {
    setFeedName(sourceTitle);
  }, [sourceTitle]);

  const handleDeleteClick = (e: any, deleteCollection = false) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setDeleteCollectionMode(deleteCollection);
    setShowDeleteModal(true);
  };

  const onDeleteModalClose = () => {
    setShowDotsMenu(false);
    setShowDeleteModal(false);
    setDeleteCollectionMode(false);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  return (
    <div className={cx(styles.rowWrapper, embedded && styles.embeddedRowWrapper, hasReferencesSection && !embedded && styles.groupParentRowWrapper)}>
      <div
        className={cx(
          styles.container,
          showDotsMenu && styles.selected,
          embedded && styles.embeddedContainer,
          isReferenceChild && styles.referenceChild,
          showExpandedReferences && styles.parentExpanded,
        )}
        style={{ position: 'relative' }}
        onClick={(e) => {
          redirect(e, feed, i);
        }}
        onContextMenu={switchDotsMenu}
      >
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
          }}
        >
          <div className={styles.menuOption} onClick={enableFeedNameEdit}>
            <EditButton /> Rename
          </div>
          <div className={styles.menuOption} onClick={handleShareClick}>
            <ShareButton /> Share
          </div>
          {canReplaceThumbnail && (
            <div className={styles.menuOption} onClick={onReplaceThumbnail}>
              <ThumbnailButton /> Replace Thumbnail Image
            </div>
          )}
          {isCollectionRow && (
            <div className={styles.menuOption} onClick={(e) => handleShareClick(e, 'single')}>
              <ShareButton /> Share Collection Only
            </div>
          )}
          {isCollectionRow ? (
            <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, true)}>
              <TrashButton /> Delete collection
            </div>
          ) : (
            <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, false)}>
              <TrashButton /> Delete
            </div>
          )}
        </div>
        <div className={styles.leftSide} onClick={(e) => redirect(e, feed, i)}>
          <FileTypeThumbnail
            className={isCollectionTimelineRow ? undefined : styles.fileTypeThumbnail}
            iconOnly={isCollectionTimelineRow}
            icon={
              <Icon onClick={(e) => redirect(e, feed, i)}>
                {feed.status !== VIDEOFEED_STATUS.TRANSCODING && feed.status !== VIDEOFEED_STATUS.DONE ? (
                  <Tooltip title={'Video is not ready'}>
                    <div>
                      <NotReadyIcon className={styles.notReadyIcon} />
                    </div>
                  </Tooltip>
                ) : feed?.type === VIDEOFEED_TYPE.MY_COMPOSER || feed?.type === VIDEOFEED_TYPE.COMPOSER ? (
                  <ComposerIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                ) : forceBlueTimelineIcon || isReferenceChild ? (
                  <TimelineIcon
                    className={cx(styles.contentTypeIcon, isCollectionTimelineRow && styles.collectionTimelineIcon)}
                    style={{ pointerEvents: 'none', color: '#39b3ea' }}
                  />
                ) : (
                  <MovieIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                )}
              </Icon>
            }
          >
            {feed.status === VIDEOFEED_STATUS.DONE && previewUrl ? (
              <img alt={'preview url'} className={styles.thumbnail} onClick={(e) => redirect(e, feed, i)} src={previewUrl} />
            ) : (
              <div className={styles.imgContainer}>
                <NotReady className={styles.thumbnail} onClick={(e: any) => redirect(e, feed, i)} />
                {feed.status === VIDEOFEED_STATUS.TRANSCODING && (
                  <Tooltip title="Processing">
                    <div className={styles.progress}>
                      <LinearProgress
                        sx={{
                          width: '100%',
                          [`&.${linearProgressClasses.colorPrimary}`]: {
                            backgroundColor: '#F5F5F5',
                          },
                          [`& .${linearProgressClasses.bar}`]: {
                            backgroundColor: '#6500AA',
                          },
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </FileTypeThumbnail>
          <Tooltip title={sourceTitle}>
            <div onClick={(e) => redirect(e, feed, i)} className={styles.name}>
              {isEditing ? (
                <form
                  onSubmit={handleEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <input ref={inputRef} className={styles.input} onChange={handleChange} defaultValue={sourceTitle} onBlur={handleEdit} />
                </form>
              ) : (
                <div>{sourceTitle}</div>
              )}
            </div>
          </Tooltip>
        </div>
        <div className={styles.team} onClick={(e) => redirect(e, feed, i)}>
          {feed.status === VIDEOFEED_STATUS.TRANSCODING && <div className={styles.statusBlue}>Processing</div>}
          {feed.status === VIDEOFEED_STATUS.DONE && <div className={styles.statusGreen}>Uploaded</div>}
          {feed.status !== VIDEOFEED_STATUS.TRANSCODING && feed.status !== VIDEOFEED_STATUS.DONE && (
            <Tooltip placement="bottom-start" title={feed.status}>
              <div className={styles.statusRed}>Draft</div>
            </Tooltip>
          )}
        </div>
        <div className={styles.access} onClick={(e) => redirect(e, feed, i)}>
          {feed?.highest_user_access ? ACCESS_TYPE?.[feed?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM] : 'View'}
        </div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(feed, dateSortOption), true)}`}>
          <div className={styles.date} onClick={(e) => redirect(e, feed, i)}>
            {DateFormatter(getDateColumnValue(feed, dateSortOption), true)}
          </div>
        </Tooltip>
        <div className={styles.size} onClick={(e) => redirect(e, feed, i)}>
          <img src={StorageIcon} alt="size" />
          {isCollectionTimelineRow ? '0 MB' : `${bytesToGB(rawSizeBytes, 2) || 0.01} GB`}
        </div>

        <div className={styles.duration} style={{ fontSize: shouldShowRefDuration ? '10px' : '12px' }}>
          {feed?.duration && (
            <Tooltip title={convertSeconds(feed.duration, false, true)}>
              <div>{shouldShowRefDuration ? `REF ${secondsToHours(feed.duration)}` : secondsToHours(feed.duration)}</div>
            </Tooltip>
          )}
        </div>

        <div className={styles.dotsMenuBtn}>
          {hasReferencesSection && !embedded && (
            <div
              className={styles.expandIcon}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setReferencesExpanded((prev) => !prev);
              }}
            >
              {referencesExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </div>
          )}
          <div className={styles.dotIcon} onClick={switchDotsMenu}>
            <MoreIcon />
          </div>
        </div>

        <DeleteVideofeedModal
          clearLocalStorageData={() => {}}
          open={showDeleteModal}
          switchModal={onDeleteModalClose}
          videofeedId={String(feed.id)}
          hasReferenceTimelines={deleteCollectionMode}
          deleteCollection={deleteCollectionMode}
          fetchAll={fetchDataCallback}
        />
        <ReplaceThumbnailModal
          open={showThumbnailModal}
          setOpen={setShowThumbnailModal}
          onUpload={(data) => uploadVideofeedThumbnail(feed.id, data)}
          onUploaded={(updatedFeed) => setThumbnailUrl(updatedFeed.thumbnail_url || '')}
          onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(feed.id) : undefined}
          preview={{ type: 'file', title: feed.title, imageUrl: previewUrl }}
        />
      </div>

      {showExpandedReferences && (
        <div className={styles.referencesList}>
          {displayReferenceTimelines.map((timeline) => (
            <AllContentVideo
              key={`timeline-${timeline.id}`}
              feed={timeline}
              i={feedIndexById?.[String(timeline.id)] || i}
              fetchDataCallback={fetchDataCallback}
              isReferenceChild
              embedded
              forceBlueTimelineIcon
              referenceTimelines={[]}
              feedIndexById={feedIndexById}
              dateSortOption={dateSortOption}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AllContentVideo;
